import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Upload, CheckCircle2, AlertCircle, PlayCircle, ShieldCheck, X } from "lucide-react";

const IMPORTS = [
  { key: "handwoven", path: "/admin/import/handwoven", kind: "handwoven", label: "OPEN_PO_handwoven CSV", hint: "Handwoven weaver POs (16 POs, 323 line items)" },
  { key: "quality", path: "/admin/import/quality", kind: "quality", label: "OPEN_PO_quality CSV", hint: "Quality hand-knotted POs (30 POs, 65 line items)" },
  { key: "lsd", path: "/admin/import/lsd", kind: "lsd", label: "LSD_client_orders CSV", hint: "Client order book (474 lines)" },
];

export default function ImportData() {
  const { t } = useLang();
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState({});
  const [preflight, setPreflight] = useState({}); // key -> preflight result
  const [pending, setPending] = useState({}); // key -> {file}
  const [imported, setImported] = useState({});
  const inputs = useRef({});
  const [runResult, setRunResult] = useState(null);

  const onFile = async (cfg, file) => {
    setBusy({ ...busy, [cfg.key]: true });
    setPreflight((p) => ({ ...p, [cfg.key]: null }));
    setPending((p) => ({ ...p, [cfg.key]: { file } }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/import/preflight?kind=${cfg.kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreflight((p) => ({ ...p, [cfg.key]: r.data }));
    } catch (e) {
      setPreflight((p) => ({ ...p, [cfg.key]: { ok: false, error: e?.response?.data?.detail || "Preflight failed" } }));
    } finally {
      setBusy({ ...busy, [cfg.key]: false });
    }
  };

  const doImport = async (cfg) => {
    const rec = pending[cfg.key];
    if (!rec) return;
    setBusy({ ...busy, [cfg.key]: true });
    try {
      const fd = new FormData();
      fd.append("file", rec.file);
      const r = await api.post(cfg.path + (replace ? "?replace=true" : ""), fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImported((s) => ({ ...s, [cfg.key]: { ok: true, data: r.data } }));
      setPending((p) => ({ ...p, [cfg.key]: null }));
      setPreflight((p) => ({ ...p, [cfg.key]: null }));
    } catch (e) {
      setImported((s) => ({ ...s, [cfg.key]: { ok: false, error: e?.response?.data?.detail || "Import failed" } }));
    } finally {
      setBusy({ ...busy, [cfg.key]: false });
    }
  };

  const cancelPending = (cfg) => {
    setPending((p) => ({ ...p, [cfg.key]: null }));
    setPreflight((p) => ({ ...p, [cfg.key]: null }));
  };

  const runChecks = async () => {
    setRunResult("running");
    try {
      const r = await api.post("/admin/run-checks");
      setRunResult(`OK · ${r.data.ran_at}`);
    } catch (e) {
      setRunResult(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="eyebrow">Admin</div>
      <h1 className="text-2xl font-bold" style={{ color: "var(--brand-navy)" }}>Data Import & Jobs</h1>
      <p className="text-sm text-[#4A627A] mt-1">Upload the three CSVs from the operations team. Each file is validated before it is inserted — no bad columns leak through.</p>

      <label className="mt-4 inline-flex items-center gap-2 text-sm">
        <input data-testid="import-replace-toggle" type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} className="w-5 h-5" />
        Replace existing records in this category
      </label>

      <div className="mt-4 space-y-4">
        {IMPORTS.map((cfg) => {
          const pf = preflight[cfg.key];
          const pnd = pending[cfg.key];
          const imp = imported[cfg.key];
          return (
            <div key={cfg.key} data-testid={`import-card-${cfg.key}`} className="hag-surface p-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="font-semibold" style={{ color: "var(--brand-navy)" }}>{cfg.label}</div>
                  <div className="text-xs text-[#7C8E9E] mt-1">{cfg.hint}</div>
                </div>
                <input
                  ref={(el) => (inputs.current[cfg.key] = el)}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(cfg, f); e.target.value = ""; }}
                />
                <button
                  data-testid={`import-btn-${cfg.key}`}
                  disabled={busy[cfg.key]}
                  onClick={() => inputs.current[cfg.key]?.click()}
                  className="h-10 px-4 rounded-md font-semibold text-white flex items-center gap-2 disabled:opacity-50 text-sm"
                  style={{ background: "var(--brand-navy)" }}
                >
                  <Upload size={14} /> {busy[cfg.key] ? "Checking…" : "Choose CSV"}
                </button>
              </div>

              {pf && (
                <div className={`mt-4 border rounded-md p-3 ${pf.ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
                  <div className="flex items-center gap-2">
                    {pf.ok ? <ShieldCheck size={16} className="text-emerald-700" /> : <AlertCircle size={16} className="text-red-700" />}
                    <div className={`text-sm font-semibold ${pf.ok ? "text-emerald-800" : "text-red-800"}`}>
                      {pf.ok ? `Preflight OK · ${pf.row_count} rows detected` : `Preflight failed`}
                    </div>
                    <button onClick={() => cancelPending(cfg)} className="ml-auto text-xs text-[#7C8E9E]" data-testid={`cancel-${cfg.key}`}><X size={14} /></button>
                  </div>
                  {pf.error && <div className="mt-2 text-xs text-red-700">{pf.error}</div>}
                  {pf.required_missing?.length > 0 && (
                    <div className="mt-2 text-xs text-red-700">
                      <b>Missing required fields:</b>{" "}
                      {pf.required_missing.map((m) => (
                        <span key={m.field} className="inline-block mr-2">
                          {m.field} <span className="text-red-500">(expected: {m.expected_headers.join(", ")})</span>
                        </span>
                      ))}
                    </div>
                  )}
                  {pf.required_resolved && Object.keys(pf.required_resolved).length > 0 && (
                    <div className="mt-2 text-xs text-emerald-800">
                      <b>Resolved:</b>{" "}
                      {Object.entries(pf.required_resolved).map(([k, v]) => (
                        <span key={k} className="inline-block mr-2">{k}=<span className="font-mono">{v}</span></span>
                      ))}
                    </div>
                  )}
                  {pf.optional_resolved && Object.keys(pf.optional_resolved).length > 0 && (
                    <div className="mt-2 text-xs text-[#4A627A]">
                      <b>Optional resolved:</b>{" "}
                      {Object.entries(pf.optional_resolved).slice(0, 12).map(([k, v]) => (
                        <span key={k} className="inline-block mr-2">{k}=<span className="font-mono">{v}</span></span>
                      ))}
                    </div>
                  )}
                  {pf.warnings && (
                    <div className="mt-2 text-xs text-amber-800">
                      <b>Warnings:</b> invalid_po_rows={pf.warnings.invalid_po_rows ?? 0} · missing_eta_rows={pf.warnings.missing_eta_rows ?? 0} · blank_ship_dates={pf.warnings.blank_ship_dates ?? 0}
                    </div>
                  )}
                  {pf.ok && pnd && (
                    <button
                      data-testid={`confirm-import-${cfg.key}`}
                      disabled={busy[cfg.key]}
                      onClick={() => doImport(cfg)}
                      className="mt-3 h-10 px-4 rounded-md font-semibold text-white flex items-center gap-2 text-sm"
                      style={{ background: "var(--brand-navy)" }}
                    >
                      <CheckCircle2 size={14} /> {busy[cfg.key] ? "Importing…" : "Confirm & Import"}
                    </button>
                  )}
                </div>
              )}

              {imp?.ok && (
                <div className="mt-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-2 flex items-start gap-1">
                  <CheckCircle2 size={14} className="mt-0.5" />
                  <div>
                    <b>Import complete.</b>{" "}
                    {Object.entries(imp.data).map(([k, v]) => <span key={k} className="mr-3">{k}: <b>{v}</b></span>)}
                  </div>
                </div>
              )}
              {imp?.ok === false && (
                <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2 flex items-start gap-1">
                  <AlertCircle size={14} className="mt-0.5" /> {imp.error}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 hag-surface p-5">
        <div className="eyebrow">Scheduled checks</div>
        <div className="text-sm mt-1 text-[#4A627A]">
          Runs daily at 08:00 UTC (ETA-approaching), 08:30 UTC (visit-overdue), 09:00 UTC (pending assignment {'>'}24h), and Saturday 06:00 UTC (Weekly MIS). Trigger manually below to test.
        </div>
        <button
          data-testid="run-checks-btn"
          onClick={runChecks}
          className="mt-3 h-10 px-4 rounded-md text-white font-semibold flex items-center gap-2"
          style={{ background: "var(--brand-navy)" }}
        >
          <PlayCircle size={16} /> Run now
        </button>
        {runResult && <div className="mt-2 text-sm text-[#4A627A]">{runResult === "running" ? t("loading") : runResult}</div>}
      </div>
    </div>
  );
}
